import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { validatePassword, PasswordStrengthBar } from '../utils/passwordUtils';
import PasswordInput from '../components/Auth/PasswordInput';
import type { StaffInviteInfo } from '@vedjegy/shared';
import { ROLE_LABELS } from '@vedjegy/shared';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px',
  border: '1px solid var(--border)', borderRadius: 8,
  fontSize: 14, boxSizing: 'border-box',
};

export default function StaffInviteAcceptPage() {
  const { token }  = useParams<{ token: string }>();
  const navigate   = useNavigate();
  const { refresh } = useAuth();

  const [info, setInfo]               = useState<StaffInviteInfo | null>(null);
  const [loadError, setLoadError]     = useState('');
  const [password, setPassword]       = useState('');
  const [confirm, setConfirm]         = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) return;
    api.staffInvite.getInfo(token)
      .then(setInfo)
      .catch((err: unknown) => {
        const msg = (err as { message?: string })?.message ?? 'Érvénytelen vagy lejárt meghívó.';
        setLoadError(msg);
      });
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const pwErr = validatePassword(password);
    if (pwErr) { setSubmitError(pwErr); return; }
    if (password !== confirm) { setSubmitError('A két jelszó nem egyezik.'); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.staffInvite.accept(token!, password);
      // Cookie is now set — reload user from /me then redirect
      await refresh();
      const me = await api.auth.me();
      const roles: string[] = me.roles ?? (me.role ? [me.role] : []);
      const isAdmin = roles.some((r) => ['tenant_admin', 'super_admin'].includes(r));
      navigate(isAdmin ? '/admin/applications' : '/reviewer/applications', { replace: true });
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? 'A fiók létrehozása sikertelen.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--background)', padding: 20,
    }}>
      <div style={{
        width: '100%', maxWidth: 440, background: '#fff',
        borderRadius: 'var(--radius-md)', padding: 36,
        boxShadow: 'var(--shadow-md)', border: '1px solid var(--border)',
      }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>
            Munkatársi meghívó
          </h1>
          {info && (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--muted-foreground)', lineHeight: 1.6 }}>
              Meghívták Önt <strong>{ROLE_LABELS[info.role] ?? info.role}</strong> szerepkörbe.<br />
              E-mail: <strong>{info.email}</strong><br />
              Az aktiváláshoz adjon meg egy jelszót.
            </p>
          )}
        </div>

        {loadError && (
          <div style={{
            padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5',
            borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 20,
          }}>
            {loadError}
          </div>
        )}

        {info && (
          <form onSubmit={(e) => void handleSubmit(e)}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
                E-mail cím
              </label>
              <input
                value={info.email}
                disabled
                style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280' }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
                Jelszó <span style={{ color: 'var(--destructive)' }}>*</span>
              </label>
              <PasswordInput
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 kar., kis/nagybetű, szám, spec."
                required
                style={inputStyle}
              />
              <PasswordStrengthBar password={password} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
                Jelszó megerősítése <span style={{ color: 'var(--destructive)' }}>*</span>
              </label>
              <PasswordInput
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Jelszó ismétlése"
                required
                style={inputStyle}
              />
            </div>

            {submitError && (
              <div style={{
                marginBottom: 16, padding: '10px 14px', background: '#fef2f2',
                border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 13,
              }}>
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%', padding: '12px', background: 'var(--primary)',
                color: '#fff', border: 'none', borderRadius: 'var(--radius-pill)',
                fontWeight: 700, fontSize: 15, cursor: submitting ? 'default' : 'pointer',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? 'Fiók létrehozása…' : 'Fiók aktiválása →'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
